<script lang="ts" setup>
import { toRef, computed } from 'vue';
import { EnumerationEvaluationOptionDto, EnumerationEvaluationParameterDto } from '@/api/generator';
import VeeTextarea from '@/components/forms/Textarea/VeeTextarea.vue';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { useField, useFieldArray } from 'vee-validate';
import EvaluationsManualTasksEnumTileParamEvaluationStateTile from './EvaluationsManualTasksEnumTileParamEvaluationStateTile.vue';
import { mdiDragHorizontal } from '@mdi/js';
import { Sortable } from 'sortablejs-vue3';
import { SortableEvent, SortableOptions } from 'sortablejs';
import EnumOrQuantityLabel from './EnumOrQuantityLabel.vue';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';
import InlineNotification from '@/components/notifications/InlineNotification.vue';
import { useValidateFieldErrorMessage } from '@/components/forms/useValidateFieldErrorMessage';
import { translateEntityProperty } from '@/utils/helper';
import { I18nKey } from '@/utils/typings';
import EvaluationCard from './EvaluationCard.vue';

interface EvaluationsManualTasksEnumTileProps {
  name: string; // evaluationscopes[0].evaluationparameters[{index}]
}

const props = defineProps<EvaluationsManualTasksEnumTileProps>();

const mode = useFormMode();

const emits = defineEmits<{
  remove: [param: EnumerationEvaluationParameterDto];
}>();

const { value: tileData } = useField<EnumerationEvaluationParameterDto>(toRef(() => props.name));

const { fields: stateArray, move } = useFieldArray<EnumerationEvaluationOptionDto>(
  toRef(() => `${props.name}.options`),
);

const { validate: validateOptions, errorMessage: optionsErrorMessage } =
  useValidateFieldErrorMessage(toRef(() => `${props.name}.options`));

function handleRemove() {
  emits('remove', tileData.value);
}

function sortingEndHandler(e: SortableEvent) {
  if (e.oldIndex !== undefined && e.newIndex !== undefined) {
    move(e.oldIndex, e.newIndex);
  }
}

const sortableOptions = computed<SortableOptions>(() => ({
  handle: '.handle',
  disabled: mode.value === 'details',
  scroll: true,
  scrollSensitivity: 300,
  forceFallback: true,
}));

const showActions = computed(() => mode.value !== 'details');
</script>

<template>
  <EvaluationCard data-testid="EvaluationEnumTile">
    <template #leading>
      <v-icon
        v-if="showActions"
        :icon="mdiDragHorizontal"
        class="handle cursor-move"
        data-testid="enum-drag"
      />
    </template>

    <template #title>
      {{ translateEntityProperty('name', tileData) }}
    </template>

    <template v-if="showActions" #actions>
      <PtwDeleteButton data-testid="enum-delete" @delete="handleRemove" />
    </template>

    <div class="enum-column">
      <p class="section-heading">{{ $t('Enumeration_one') }}</p>
      <EnumOrQuantityLabel :data="tileData" :label="$t('Enumeration_one')" />
      <div data-testid="enum-instruction">
        <p class="section-heading">{{ $t('InstructionForTaskExecution' as I18nKey) }}</p>
        <VeeTextarea :label="$t('Instruction_one')" :name="`${name}.instruction`" :mode="mode" />
      </div>
    </div>

    <div data-testid="enum-results">
      <p class="section-heading">{{ $t('Enumeration_one') }}</p>
      <p class="section-hint">{{ $t('EnumerationEvaluationHint' as I18nKey) }}</p>

      <div class="enum-list-header" :class="{ 'is-details': mode === 'details' }">
        <span>{{ $t('EnumerationItem_other') }}</span>
        <span class="enum-list-header__result">{{ $t('EvaluationResult') }}</span>
      </div>

      <InlineNotification
        v-if="optionsErrorMessage"
        class="mb-2"
        :value="{ type: 'error', message: optionsErrorMessage }"
      />

      <Sortable
        class="w-100"
        :list="stateArray"
        :options="sortableOptions"
        itemKey="key"
        @end="sortingEndHandler"
      >
        <template #item="{ index, key }">
          <EvaluationsManualTasksEnumTileParamEvaluationStateTile
            :key="key"
            :name="`${props.name}.options[${index}]`"
            @enabled="validateOptions()"
          />
        </template>
      </Sortable>
    </div>
  </EvaluationCard>
</template>

<style lang="scss" scoped>
.enum-column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.section-heading {
  margin: 0;
  color: rgb(var(--v-theme-primary-darken-2));
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
}

.section-hint {
  margin: 8px 0 16px;
  color: rgb(var(--v-theme-primary));
  font-size: 16px;
  line-height: 20px;
}

.enum-list-header {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  padding-left: 40px;
  color: rgb(var(--v-theme-primary-lighten-1));
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
}

.enum-list-header.is-details {
  padding-left: 8px;
}

.enum-list-header__result {
  width: 286px;
  margin-left: auto;
  flex: 0 0 286px;
}
</style>
